import React, { useEffect, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Home, Ticket, MapPin, User, LogOut,
  Store, Percent, QrCode, BarChart3, Calendar, Users,
  Settings, Radio, ScrollText, Gift, ClipboardList, ShieldCheck,
  Flame, MessageSquare, Bell, Palette } from "lucide-react";

const consumerLinks = [
  { to: "/dashboard", label: "Home", icon: Home },
  { to: "/dashboard/passport", label: "Passport", icon: Ticket },
  { to: "/dashboard/checkin", label: "Check In", icon: QrCode },
  { to: "/dashboard/messages", label: "Messages", icon: MessageSquare },
  { to: "/dashboard/offers", label: "Offers", icon: Percent },
  { to: "/dashboard/prizes", label: "My Prizes", icon: Gift },
  { to: "/dashboard/profile", label: "Profile", icon: User },
];

const businessLinks = [
  { to: "/business", label: "Overview", icon: Home },
  { to: "/business/profile", label: "Profile", icon: Store },
  { to: "/business/messages", label: "Messages", icon: MessageSquare },
  { to: "/business/offers", label: "Offers", icon: Percent },
  { to: "/business/events", label: "Events", icon: Calendar },
  { to: "/business/qr", label: "QR Center", icon: QrCode },
  { to: "/business/leads", label: "Leads", icon: Users },
  { to: "/business/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/business/boost", label: "Streak Boost Shop", icon: Flame },
];

const adminLinks = [
  { to: "/admin", label: "Overview", icon: Home },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/businesses", label: "Businesses", icon: Store },
  { to: "/admin/sweepstakes", label: "Sweepstakes", icon: Ticket },
  { to: "/admin/prizes", label: "Prizes & Draw", icon: Gift },
  { to: "/admin/winner-claims", label: "Winner Claims", icon: ClipboardList },
  { to: "/admin/livestream", label: "Livestream", icon: Radio },
  { to: "/admin/notifications", label: "Broadcast", icon: Radio },
  { to: "/admin/push-logs", label: "Push Log", icon: Bell },
  { to: "/admin/scheduled", label: "Prize Drops", icon: Calendar },
  { to: "/admin/streak-boosts", label: "Streak Boosts", icon: Flame },
  { to: "/admin/sign-ins", label: "Sign-In Log", icon: ShieldCheck },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText },
  { to: "/admin/brand", label: "Brand Manager", icon: Palette },
];

function linksFor(role) {
  if (role === "admin") return adminLinks;
  if (role === "business") return businessLinks;
  return consumerLinks;
}

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const links = linksFor(user?.role || "consumer");
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/chat/unread");
        if (!cancelled) setUnread(data.unread || 0);
      } catch {}
    };
    poll();
    const t = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  const badge = (label) => {
    if (label !== "Messages" || unread <= 0) return null;
    const shown = unread > 9 ? "9+" : unread;
    return (
      <span data-testid="unread-badge"
        className="ml-auto bg-primary text-black text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full inline-flex items-center justify-center">
        {shown}
      </span>
    );
  };
  const dot = (label) => (label === "Messages" && unread > 0) ? (
    <span data-testid="unread-dot" className="absolute top-1 right-4 w-2 h-2 rounded-full bg-primary"/>
  ) : null;

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#070c1a] overflow-x-clip w-full max-w-full">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex md:w-64 flex-col border-r border-adn-border bg-[#0a1226]">
        <Link to="/" className="flex items-center justify-center px-4 py-5 border-b border-adn-border group">
          <img src="/logo.png" alt="MyADN Local FanFest"
            className="h-20 w-auto object-contain
                       drop-shadow-[0_0_22px_rgba(0,211,127,0.55)]
                       group-hover:drop-shadow-[0_0_32px_rgba(0,211,127,0.85)]
                       transition-all duration-300" />
        </Link>
        <nav className="flex-1 py-4">
          {links.map(l => (
            <NavLink key={l.to} to={l.to} end
              data-testid={`side-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
              className={({isActive}) =>
                `flex items-center gap-3 px-6 py-3 text-sm border-l-2 ${isActive ? "border-primary text-primary bg-primary/5" : "border-transparent text-white/70 hover:text-white hover:bg-white/5"}`}>
              <l.icon size={16} /> <span>{l.label}</span>{badge(l.label)}
            </NavLink>
          ))}
        </nav>
        <button onClick={async () => { await logout(); nav("/"); }}
          data-testid="side-logout"
          className="flex items-center gap-2 px-6 py-4 border-t border-adn-border text-sm text-white/70 hover:text-primary">
          <LogOut size={16}/> Sign out
        </button>
      </aside>

      {/* Top bar (mobile) */}
      <header className="md:hidden sticky top-0 z-30 bg-[#0a1226]/95 backdrop-blur border-b border-adn-border pt-[env(safe-area-inset-top)]">
        <div className="flex items-center justify-between gap-2 px-3 py-3 max-w-full">
          <Link to="/" className="flex items-center min-w-0 flex-1">
            <img src="/logo.png" alt="MyADN Local FanFest"
              className="h-12 w-auto object-contain
                         drop-shadow-[0_0_14px_rgba(0,211,127,0.55)] shrink-0" />
          </Link>
          <div className="flex items-center gap-1.5 shrink-0">
            <Link to={user?.role === "admin" ? "/admin" : user?.role === "business" ? "/business/profile" : "/dashboard/profile"}
              data-testid="mobile-profile-link"
              className="p-2 rounded-md border border-adn-border text-white/80 hover:text-primary hover:border-primary"
              aria-label="Profile & Settings">
              <User size={16}/>
            </Link>
            <button onClick={async () => { await logout(); nav("/"); }}
              data-testid="mobile-logout"
              className="p-2 rounded-md border border-adn-border text-white/80 hover:text-primary hover:border-primary"
              aria-label="Sign out">
              <LogOut size={16}/>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 min-w-0 pb-[calc(env(safe-area-inset-bottom)+96px)] md:pb-8">
        <div className="max-w-6xl mx-auto p-4 sm:p-6 md:p-8">{children}</div>
      </main>

      {/* Bottom tab bar (mobile) */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-[#0a1226]/95 backdrop-blur border-t border-adn-border flex justify-around items-stretch py-1.5 pb-[calc(env(safe-area-inset-bottom)+6px)]">
        {links.slice(0, 5).map(l => (
          <NavLink key={l.to} to={l.to} end
            data-testid={`tab-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
            className={({isActive}) =>
              `relative flex flex-col items-center justify-center flex-1 gap-1 px-1 min-h-[56px] ${isActive ? "text-primary" : "text-white/60"}`}>
            <l.icon size={20}/>
            <span className="text-[10px] tracking-wider font-medium leading-none whitespace-nowrap">
              {l.label.toUpperCase()}
            </span>
            {dot(l.label)}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
